import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import {
    RateReview,
    Star,
    StarBorder
} from "@mui/icons-material";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Pagination,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import axios from "axios";

export default function Reviews() {
    const apiURL = process.env.REACT_APP_API;
    const navigate = useNavigate();
    const theme = useTheme();

    const [reviews, setReviews] = useState([]);
    const [totalReviews, setTotalReviews] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterRating, setFilterRating] = useState('all');
    const [loading, setLoading] = useState(true);

    const colors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.info.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main
    ];

    useEffect(() => {
        fetchReviews();
    }, [currentPage, filterRating]);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            let url = `${apiURL}/public/review/getall?page=${currentPage}&limit=12`;
            if (filterRating !== 'all') {
                url += `&rating=${filterRating}`;
            }
            const response = await axios.get(url);
            if (response.data.status) {
                const reviewsWithColors = response.data.data.map((review, index) => ({
                    ...review,
                    color: colors[index % colors.length]
                }));
                setReviews(reviewsWithColors);
                setTotalReviews(response.data.pagination.totalRecords);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleFilterChange = (event) => {
        setFilterRating(event.target.value);
        setCurrentPage(1);
    };

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: "50vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    overflow: "hidden",
                    pt: { xs: 4, md: 0 }
                }}
            >
                {/* Decorative Elements */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "-20%",
                        right: "-10%",
                        width: "500px",
                        height: "500px",
                        borderRadius: "50%",
                        background: `${theme.palette.secondary.main}20`,
                        filter: "blur(80px)"
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "-20%",
                        left: "-10%",
                        width: "400px",
                        height: "400px",
                        borderRadius: "50%",
                        background: `${theme.palette.info.main}20`,
                        filter: "blur(80px)"
                    }}
                />

                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack p={2} spacing={3} alignItems="center" textAlign="center">
                        {/* Icon */}
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <RateReview sx={{ fontSize: '2.5rem', color: '#fff' }} />
                        </Box>

                        {/* Title */}
                        <Typography
                            sx={{
                                fontSize: { xs: '2rem', md: '3rem' },
                                fontWeight: 900,
                                color: '#fff',
                                lineHeight: 1.2
                            }}
                        >
                            Student Reviews
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                            sx={{
                                fontSize: { xs: '1rem', md: '1.2rem' },
                                color: 'rgba(255, 255, 255, 0.9)',
                                maxWidth: '600px',
                                lineHeight: 1.6
                            }}
                        >
                            Real feedback from students who have transformed their careers with us
                        </Typography>

                        {/* Stats */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 4,
                                mt: 2,
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    sx={{
                                        fontSize: '2rem',
                                        fontWeight: 900,
                                        color: '#fff',
                                        lineHeight: 1
                                    }}
                                >
                                    {totalReviews}+
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        mt: 0.5
                                    }}
                                >
                                    Total Reviews
                                </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} sx={{ fontSize: '1.5rem', color: theme.palette.warning.main }} />
                                    ))}
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: '0.9rem',
                                        color: 'rgba(255, 255, 255, 0.8)',
                                        mt: 0.5
                                    }}
                                >
                                    Average Rating
                                </Typography>
                            </Box>
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* Reviews Section */}
            <Box
                sx={{
                    py: { xs: 6, md: 10 },
                    background: '#f5f5f5'
                }}
            >
                <Container maxWidth="lg">
                    <Stack >
                        {/* Reviews Grid */}
                        {loading ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        color: theme.palette.text.secondary
                                    }}
                                >
                                    Loading reviews...
                                </Typography>
                            </Box>
                        ) : reviews.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.2rem',
                                        color: theme.palette.text.secondary
                                    }}
                                >
                                    No reviews found
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={3}>
                                {reviews.map((review, index) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                        <Card
                                            sx={{
                                                height: '100%',
                                                background: '#fff',
                                                borderRadius: 3,
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer',
                                                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: `0 12px 32px ${review.color}25`,
                                                    '& .review-avatar': {
                                                        transform: 'scale(1.1)',
                                                        background: review.color,
                                                        color: '#fff'
                                                    }
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: 3 }}>
                                                <Stack spacing={2}>
                                                    {/* Avatar and Student Info */}
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <Box
                                                            className="review-avatar"
                                                            sx={{
                                                                width: 50,
                                                                height: 50,
                                                                borderRadius: '50%',
                                                                background: `${review.color}15`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '1.1rem',
                                                                fontWeight: 700,
                                                                color: review.color,
                                                                transition: 'all 0.3s ease',
                                                                flexShrink: 0,
                                                                overflow: 'hidden'
                                                            }}
                                                        >
                                                            <img
                                                                src={`/spywareassets/student/${review.studentid}.jpg`}
                                                                alt={review.student_name}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '50%'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.parentElement.textContent = review.student_name.charAt(0).toUpperCase();
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '0.95rem',
                                                                    fontWeight: 700,
                                                                    color: theme.palette.text.primary,
                                                                    lineHeight: 1.2,
                                                                    mb: 0.3,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {review.student_name}
                                                            </Typography>
                                                            <Chip
                                                                label={review.course_title}
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 600,
                                                                    background: `${review.color}15`,
                                                                    color: review.color,
                                                                    border: 'none',
                                                                    maxWidth: '100%'
                                                                }}
                                                            />
                                                        </Box>
                                                    </Stack>

                                                    {/* Rating */}
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {[...Array(5)].map((_, i) => (
                                                            i < review.rating ? (
                                                                <Star key={i} sx={{ fontSize: '1rem', color: theme.palette.warning.main }} />
                                                            ) : (
                                                                <StarBorder key={i} sx={{ fontSize: '1rem', color: theme.palette.grey[300] }} />
                                                            )
                                                        ))}
                                                    </Box>

                                                    {/* Review Text */}
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            color: theme.palette.text.secondary,
                                                            lineHeight: 1.6,
                                                            minHeight: '100px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 5,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}
                                                    >
                                                        "{review.review}"
                                                    </Typography>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="large"
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            fontWeight: 600,
                                            fontSize: '1rem'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Stack>
                </Container>
            </Box>
        </>
    );
}
